import { Link } from "react-router-dom";
import Button from "@/components/base/Button";
import PageHero from "@/components/base/PageHero";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import ProcessFlow from "@/components/base/ProcessFlow";
import { useUi } from "@/context/UiContext";
import Seo from "@/components/base/Seo";

const CAPABILITIES = [
  {
    icon: "ri-focus-3-line",
    title: "整理行動卡點",
    desc: "拖延、無法開始、容易中斷、缺乏方向、自我懷疑。",
  },
  {
    icon: "ri-compass-3-line",
    title: "了解幸福人才方法",
    desc: "三大系統、五階成長與相關觀念。",
  },
  {
    icon: "ri-calendar-2-line",
    title: "查詢課程與活動",
    desc: "近期活動、內容、適合對象與報名方式。",
  },
  {
    icon: "ri-file-search-line",
    title: "找到相關內容",
    desc: "依問題推薦網站內容與學習資源。",
  },
  {
    icon: "ri-team-line",
    title: "整理企業需求",
    desc: "協助企業初步整理問題、對象、人數與方向。",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "引導人工聯繫",
    desc: "需要正式報價或客製合作時導向 LINE／Form。",
  },
];

const EXAMPLES = [
  "我每天很忙，但一直覺得沒有方向。",
  "我總是設定很多目標，最後都沒完成。",
  "幸福人才的信念系統是什麼？",
  "我第一次接觸，適合從哪裡開始？",
  "我們公司想做主管培訓。",
];

const PROVIDES = ["一般資訊", "初步問題整理", "幸福人才相關內容", "課程／企業服務導引"];
const NOT_PROVIDES = ["心理疾病診斷", "醫療診斷", "法律判斷", "財務專業判斷", "重大人生決策替代"];

type QuickItem = { label: string; type: "quiz" } | { label: string; type: "link"; to: string };

const QUICK_QUESTIONS: QuickItem[] = [
  { label: "我想知道自己為什麼一直做不到", type: "quiz" },
  { label: "我想做 30 秒行動卡點快篩", type: "quiz" },
  { label: "我想了解幸福人才養成系統", type: "link", to: "/happiness-talent-system" },
  { label: "我想找適合我的課程", type: "link", to: "/courses" },
  { label: "我想詢問企業培訓", type: "link", to: "/contact#corporate-form" },
];

export default function AiCoach() {
  const { openQuiz, openAi } = useUi();

  return (
    <>
      <Seo
        title="AI 幸福人才顧問｜行動卡點・課程與企業培訓初步整理"
        description="AI 幸福人才顧問協助你初步整理行動卡點、了解幸福人才方法、查詢課程活動與整理企業培訓需求，先整理再決定下一步。"
        keywords="AI 幸福人才顧問,行動卡點,課程,企業培訓,初步整理"
      />
      <PageHero
        eyebrow="AI 幸福人才顧問"
        title={
          <>
            有些問題，
            <br />
            可以先從一段對話開始。
          </>
        }
        description={
          <>
            <p>
              不知道自己真正卡在哪裡？不確定適合哪個課程？想了解幸福人才的方法？
              正在規畫企業培訓，但不知道從哪裡開始？可以先告訴 AI 幸福人才顧問你現在正在面對什麼。
            </p>
          </>
        }
      >
        <Button onClick={openAi} icon="ri-chat-3-line">
          開始對話
        </Button>
      </PageHero>

      {/* AI 可以幫你什麼 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="AI 可以幫你什麼" title="先整理，再決定下一步。" />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c.title} delay={i * 70}>
                <div className="h-full rounded-card bg-cream border border-line p-7 transition-transform duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 flex items-center justify-center rounded-md bg-gold-soft/70">
                    <i className={`${c.icon} text-2xl text-primary`} aria-hidden="true"></i>
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-ink">{c.title}</h3>
                  <p className="mt-2 text-sm leading-[1.8] text-ink-soft">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 你可以這樣問 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="你可以這樣問" title="不知道怎麼開頭？試試這樣說。" />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {EXAMPLES.map((e, i) => (
              <Reveal key={e} delay={i * 60}>
                <div className="flex items-start gap-3 rounded-card bg-white border border-line p-5">
                  <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-primary/10">
                    <i className="ri-question-line text-primary" aria-hidden="true"></i>
                  </span>
                  <p className="text-sm leading-[1.7] text-ink">「{e}」</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI 怎麼陪你整理 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="AI 怎麼陪你整理" title="一步一步，陪你理清楚。" />
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-10">
              <ProcessFlow
                steps={[
                  { title: "了解問題", subtitle: "先聽你現在的狀態" },
                  { title: "初步整理", subtitle: "整理可能的卡點" },
                  { title: "下一步", subtitle: "提供內容或引導" },
                ]}
              />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-10 rounded-card bg-primary-dark p-8 md:p-10">
              <p className="text-lg md:text-xl font-semibold text-white leading-[1.6]">
                AI 不會每次都直接推銷課程。
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* AI 使用界線 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="使用界線" title="AI 能做的，與不能取代的。" />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-card bg-white border border-line p-7">
                <p className="text-base font-semibold text-success">AI 提供</p>
                <ul className="mt-4 space-y-2.5">
                  {PROVIDES.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-ink">
                      <i className="ri-check-line text-success" aria-hidden="true"></i>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="h-full rounded-card bg-white border border-line p-7">
                <p className="text-base font-semibold text-ink-soft">AI 不提供</p>
                <ul className="mt-4 space-y-2.5">
                  {NOT_PROVIDES.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-ink-soft">
                      <i className="ri-close-line text-ink-soft/60" aria-hidden="true"></i>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal delay={140}>
            <p className="mt-8 text-xs leading-[1.8] text-ink-soft/70 reading-width">
              AI 回覆仍可能存在不完整或錯誤資訊，重要事項請以官方公告或人工確認為準。
            </p>
          </Reveal>
        </div>
      </section>

      {/* 開始聊天 */}
      <section className="bg-primary-dark">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-2xl md:text-[2.125rem] font-bold leading-[1.35] text-white">
                不知道答案也沒關係。
              </h2>
              <p className="mt-5 text-base leading-[1.8] text-gold-soft/90">
                先告訴我：你現在最想改變的一件事情是什麼？
              </p>

              <div className="mt-8 flex flex-wrap justify-center gap-2.5">
                {QUICK_QUESTIONS.map((q) =>
                  q.type === "quiz" ? (
                    <button
                      key={q.label}
                      onClick={openQuiz}
                      className="rounded-full bg-white/10 border border-white/20 px-4 py-2 text-sm text-white hover:border-gold hover:text-gold transition-colors cursor-pointer"
                    >
                      {q.label}
                    </button>
                  ) : (
                    <Link
                      key={q.label}
                      to={q.to}
                      className="rounded-full bg-white/10 border border-white/20 px-4 py-2 text-sm text-white hover:border-gold hover:text-gold transition-colors cursor-pointer"
                    >
                      {q.label}
                    </Link>
                  )
                )}
              </div>

              <div className="mt-9">
                <Button onClick={openAi} size="lg" icon="ri-chat-3-line">
                  開始與 AI 幸福人才顧問對話
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}